import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuill } from 'react-quilljs'
import { useParams } from 'react-router-dom'
import { Form, notification } from 'antd'
import axios from 'axios'
import Const from 'constants/constant'
import dayjs from 'dayjs'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  hasLinkErrorState,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import { currentAnnotation } from 'store/krpanoStore'
import { AlertPayload } from 'types/alert'
import {
  Annotation,
  AnnotationPayload,
  AnnotationType,
  BuildingFile,
  LinkAnnotation
} from 'types/building'
import { KrpanoInterface } from 'types/krpano'
import Utils from 'utils'
import CommonFunction from 'utils/commonFunction'
import Resizer from 'utils/imageResizer'
import Krpano from 'utils/krpano'

export default function useAnnotation() {
  const { t } = useTranslation()
  const { id, floorId } = useParams()
  const [form] = Form.useForm()
  const { navigate, getBuiildingFolderList, getDirectory } = useDirectory()
  const {
    quill,
    quillRef,
    Quill: QuillClass
  } = useQuill({
    theme: 'snow',
    modules: Const.QUILL_MODULES,
    formats: Const.QUILL_FORMATS,
    placeholder: '入力してください'
  })

  const selectedSubImage = useRecoilValue(selectedSubImageState)
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const [currentSelected, setCurrentSelected] =
    useRecoilState(currentAnnotation)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const [tempAlertData, setTempAlertData] = useState<AlertPayload>()

  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingContent, setIsFetchingContent] = useState(false)
  const hasLinkError = useRecoilValue(hasLinkErrorState)

  const [selectedFolder, setSelectedFolder] = useState<AnnotationType>(
    currentSelected?.type || AnnotationType.ARCHITECTURE
  )
  const [selectedChildrenFolder, setSelectedChildrenFolder] = useState<{
    id: AnnotationType
    label: string
  }>()
  const isDataExistsAnnotation = Krpano.IsAnnotationDataEmpty(currentSelected)

  const { trigger, isMutating } = useMutation(`v1/annotations`)
  const { trigger: getPresignedUrl, isMutating: isGettingPresignedUrl } =
    useMutation('v1/annotations/presigned-urls')
  const { trigger: deleteOldContent, isMutating: isDeletingOldContent } =
    useMutation('v1/delete-file')
  const { trigger: createAlert } = useMutation('v1/alerts')

  const { trigger: getBuildingPresignedUrl } = useMutation(
    'v1/buildings/presigned-urls'
  )
  const { trigger: saveFileMetadata } = useMutation('v1/buildings/files')

  const buildingFolder = useMemo(
    () => getBuiildingFolderList(),
    [getBuiildingFolderList]
  )
  const dataSource = useMemo(
    () => buildingFolder.find((folder) => folder.id === selectedFolder),
    [buildingFolder, selectedFolder]
  )
  const childrenFolder = useMemo(() => {
    const children = (dataSource?.children || []).filter(
      (item) => item.label !== '竣工図'
    )
    return [
      ...children,
      { label: 'リンク', id: 40, isFileHolder: true, children: null }
    ]
  }, [dataSource?.children])

  const hotspotLoadStyle = (krpano: KrpanoInterface, data: Annotation) => {
    Krpano.AddAnnotation(krpano, [data], null, getDirectory() === 'tour')
  }

  const createNewAnnotation = (krpano: KrpanoInterface, data: Annotation) => {
    krpano.call?.('hide_temp_hotspot()')
    Krpano.AddAnnotation(krpano, [data], null, getDirectory() === 'tour')
  }

  const handleBack = () => {
    if (hasLinkError && selectedChildrenFolder?.id === 40) return

    selectedChildrenFolder && setSelectedChildrenFolder(undefined)
    if (
      selectedChildrenFolder?.id === 40 &&
      !currentSelected?.link &&
      currentSelected?.name
    ) {
      form.setFieldValue('name', '')
      setCurrentSelected((prev) => ({ ...prev, name: '' }))
    }
  }

  const handleCloseModal = () => {
    notification.destroy('placingAnnotation')
    if (selectedChildrenFolder) {
      setSelectedChildrenFolder(undefined)
    }
    if (!currentSelected?.id) {
      Krpano.RemoveTempAnnotation()
    }
    setTempAlertData(undefined)
    setCurrentSelected(undefined)
  }

  const getImageID = () => {
    const isViewingSubImage = pointDetail?.subImages?.find(
      (item) => item.id === selectedSubImage
    )
    if (isViewingSubImage) {
      return selectedSubImage
    }
    return pointDetail?.image360?.id
  }

  const handleAddEditAnnotation = async (
    memo?: string,
    memo_content?: string,
    memo_file_name?: string,
    filesOverride?: BuildingFile[]
  ) => {
    const krpano = document.getElementById(
      'embedpano-full'
    ) as unknown as KrpanoInterface
    try {
      let memoKey = ''
      if (
        selectedFolder === AnnotationType.IMAGE ||
        selectedFolder === AnnotationType.INFO
      ) {
        const rawKey =
          currentSelected?.memo_content || currentSelected?.memo || ''
        memoKey = Krpano.GetMemoKeyFromPresignedUrl(rawKey)
        const isValidS3Key =
          memoKey &&
          typeof memoKey === 'string' &&
          !memoKey.startsWith('<') &&
          memoKey.length > 4 &&
          (memoKey.startsWith('annotation-content') ||
            memoKey.includes('/annotation-content'))
        if (isValidS3Key) {
          await deleteOldContent({
            data: {
              key: memoKey
            }
          })
        }
      }
      const filesToUse = filesOverride || currentSelected?.files
      const convertedValue: AnnotationPayload = {
        ...currentSelected,
        link: (currentSelected?.link || '').trim(),
        name: (currentSelected?.name || '').trim(),
        links: (currentSelected?.links || []).map(
          (item: LinkAnnotation | null) =>
            item == null
              ? { link: '', name: '' }
              : {
                  link: (item.link || '').trim(),
                  name: (item.name || '').trim()
                }
        ),
        memo_name:
          selectedFolder === AnnotationType.MEMO
            ? (currentSelected?.memo_name || '').trim()
            : '',
        memo: memo || '',
        memo_content: memo_content || '',
        memo_file_name: memo_file_name || '',
        type: selectedFolder as number,
        files: filesToUse ? filesToUse.map((item) => item.id) : [],
        imageID: getImageID()
      }
      const { data } = await trigger({
        method: 'put',
        data: {
          building_id: Number(id),
          floor_id: Number(floorId),
          point_id: currentSelectedPoint,
          annotation: convertedValue
        }
      })
      if (!currentSelected?.id) {
        setPointDetail(
          (item) =>
            item && {
              ...item,
              annotations: [...(item?.annotations || []), data]
            }
        )
        createNewAnnotation(krpano, data)
      } else {
        setPointDetail(
          (item) =>
            item && {
              ...item,
              annotations: item.annotations.map((anno) =>
                anno.id === currentSelected.id ? data : anno
              )
            }
        )
        hotspotLoadStyle(krpano, data)
      }

      // If there is temporary alert data, save it now that we have an annotation ID
      if (tempAlertData) {
        try {
          // Exclude the draft-only extra_users_data before sending to the API
          const { extra_users_data: unused, ...apiPayload } = tempAlertData
          await createAlert({
            method: 'post',
            data: {
              ...apiPayload,
              annotation_id: data.id
            }
          })
          setTempAlertData(undefined)
        } catch (alertError: unknown) {
          notification.error({
            message: 'アラートの保存に失敗しました。'
          })
        }
      }

      notification.success({
        message: t('building.saved')
      })
      setIsLoading(false)
      handleCloseModal()
    } catch (error: unknown) {
      if (krpano) {
        krpano.call('hide_temp_hotspot()')
      }
      notification.destroy('placingAnnotation')
      setIsLoading(false)
      Utils.handleErrorNavigate(error, navigate)
    }
  }

  const validateAnnotationLink = () => {
    if (selectedChildrenFolder?.id === AnnotationType.MEMO) {
      const link = currentSelected?.link
      const name = currentSelected?.name

      // TODO: regex for non special characters
      const pattern: RegExp =
        // eslint-disable-next-line no-useless-escape
        /^(http(s)?:\/\/)?(?:www\.)?[-a-zA-Z0-9…「」｛｝（）＋！ー＆＃＠〜＊＝：；？＜＞、。＄"｜・\-_!+()&*$@":;<>/?,.[\]#|=%~]{1,256}\.[a-zA-Z0-9…「」｛｝（）＋！ー＆＃＠〜＊＝：；？＜＞、。＄"｜・\-_!+()&*$@":;<>/?,.[\]#|=%~]{1,6}\b(?:[-a-zA-Z0-9…「」｛｝（）＋！ー＆＃＠〜＊＝：；？＜＞、。＄"｜・\-_!+()&*$@":;<>/?,.[\]#|=%~]*)$/
      const regexNonSpecialCha: RegExp =
        // eslint-disable-next-line no-irregular-whitespace
        /^[\u4e00-\u9fa5\u3040-\u309f\u30a0-\u30ff\da-zA-Z０-９ 　「」｛｝（）＋！ー＆＃＠〜＊＝：；？＜＞、。＄"｜・\-_!+()&*$@":;<>/?,.[\]#|=]+$/g

      const isLinkInvalid =
        link && (!pattern.test(link.trim()) || link.trim().length > 500)

      const isNameInvalid =
        name &&
        (!regexNonSpecialCha.test(name.trim()) || name.trim().length > 500)

      if (isLinkInvalid || isNameInvalid) {
        return
      }
    }
    handleAddEditAnnotation()
  }

  const handleSubmit = async (
    selectedImageFile?: File | null,
    selectedPdfFile?: File | null,
    memoHtml?: string
  ) => {
    if (selectedChildrenFolder?.id === 40) {
      try {
        await form.validateFields()
      } catch (err: unknown) {
        notification.error({
          message: 'リンク項目にエラーがあります。確認してください。'
        })
        return
      }
    }

    if (
      isMutating ||
      isGettingPresignedUrl ||
      isDeletingOldContent ||
      isFetchingContent
    )
      return
    setIsLoading(true)

    // Check if the temporary alert date has passed before proceeding with the save
    if (tempAlertData && tempAlertData.repeat_type !== 2) {
      const selectedDate = dayjs(tempAlertData.send_at)
      if (
        selectedDate.isValid() &&
        selectedDate.valueOf() <= dayjs().valueOf()
      ) {
        notification.error({
          message:
            '過去の日時は選択できません。現在以降の日時を選択してください。'
        })
        setIsLoading(false)
        return
      }
    }

    try {
      let memoContent: string = ''
      let timestamp: number | undefined
      let extension: string | undefined
      let cleanFileName: string | undefined
      let key: string | undefined

      if (selectedFolder === AnnotationType.MEMO) {
        // Use the HTML passed from QuillEditorV2 (memoHtml); fall back to
        // quill?.root.innerHTML only for legacy callers that don't pass it.
        const memoTextContent = memoHtml || quill?.root.innerHTML || ''
        const isMemoEmpty = Krpano.IsMemoEmpty(memoTextContent)
        if (isMemoEmpty) {
          notification.error({
            message: 'メモ内容を入力してください'
          })
          setIsLoading(false)
          return
        }

        if (memoTextContent && !Krpano.IsMemoEmptyCheckImage(memoTextContent)) {
          const newQuillInnerHtml = await Resizer.ResizedQuillImages(
            memoTextContent
          )
          const file = new Blob([newQuillInnerHtml], {
            type: 'text/plain'
          })
          if (file.size / (1024 * 1024) > 50) {
            notification.error({
              key: 'memoTooLarge',
              message: t('common.validationError.fileTooLarge')
            })
            setIsLoading(false)
            return
          }

          const data = await getPresignedUrl({
            data: {
              extension: 'txt',
              fileName: 'annotation-content'
            }
          })
          const { key: textKey = undefined, presignedUrl = undefined } = {
            ...data?.data?.s3PreSignedLinks[0]
          }
          await axios.put(presignedUrl, file)
          key = textKey
        }
      }

      if (selectedFolder === AnnotationType.IMAGE) {
        if (selectedImageFile) {
          const fileExtension = selectedImageFile.name
            .toLowerCase()
            .split('.')
            .pop()
          const allowedImageExtensions = ['jpg', 'jpeg', 'png', 'gif']
          if (!allowedImageExtensions.includes(fileExtension || '')) {
            setIsLoading(false)
            notification.error({
              message: t('translation.annotationImageTypeRequired')
            })
            return
          }

          const fileSizeInMB = selectedImageFile.size / (1024 * 1024)
          if (fileSizeInMB > 50) {
            setIsLoading(false)
            notification.error({
              message: t('common.validationError.fileTooLarge')
            })
            return
          }
        }
      } else if (selectedFolder === AnnotationType.MOVIE) {
        const youtubeUrl = currentSelected?.link || ''
        if (youtubeUrl.trim()) {
          const youtubeRegex =
            /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/

          if (!youtubeRegex.test(youtubeUrl)) {
            setIsLoading(false)
            notification.error({
              message: t('translation.annotationYoutubeUrlRequired')
            })
            return
          }
          memoContent = youtubeUrl
        }
      } else if (selectedFolder === AnnotationType.INFO) {
        if (selectedPdfFile) {
          const fileExtension = selectedPdfFile.name
            .toLowerCase()
            .split('.')
            .pop()
          if (fileExtension !== 'pdf') {
            setIsLoading(false)
            notification.error({
              message: t('translation.annotationPdfTypeRequired')
            })
            return
          }

          const fileSizeInMB = selectedPdfFile.size / (1024 * 1024)
          if (fileSizeInMB > 50) {
            setIsLoading(false)
            notification.error({
              message: t('common.validationError.fileTooLarge')
            })
            return
          }
        }
      }

      if (selectedFolder === AnnotationType.IMAGE && selectedImageFile) {
        timestamp = Date.now()
        extension = selectedImageFile.name.split('.').pop() || 'jpg'
        cleanFileName = selectedImageFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')

        const presignedData = await getPresignedUrl({
          data: {
            extension,
            fileName: 'annotation-content'
          }
        })

        const { key: fileKey, presignedUrl } =
          presignedData?.data?.s3PreSignedLinks[0] || {}

        if (fileKey && presignedUrl) {
          await axios.put(presignedUrl, selectedImageFile)
          memoContent = fileKey
        }
      } else if (selectedFolder === AnnotationType.INFO && selectedPdfFile) {
        timestamp = Date.now()
        extension = selectedPdfFile.name.split('.').pop() || 'pdf'
        cleanFileName = selectedPdfFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')

        const presignedData = await getPresignedUrl({
          data: {
            extension,
            fileName: 'annotation-content'
          }
        })

        const { key: fileKey, presignedUrl } =
          presignedData?.data?.s3PreSignedLinks[0] || {}

        if (fileKey && presignedUrl) {
          await axios.put(presignedUrl, selectedPdfFile)
          memoContent = fileKey
        }
      }

      if (memoContent && timestamp && extension && cleanFileName) {
        const newFile: BuildingFile = {
          id: timestamp,
          s3Path: memoContent,
          mimeType:
            selectedImageFile?.type || selectedPdfFile?.type || 'image/jpeg',
          extension,
          createdAt: new Date().toISOString(),
          name: cleanFileName,
          thumbnail: memoContent,
          pointId: null,
          takenAt: null
        }

        setCurrentSelected((prev) => ({
          ...prev,
          files: prev?.files ? [...prev.files, newFile] : [newFile]
        }))
      }

      let finalFiles = currentSelected?.files

      if (currentSelected?.files) {
        const pendingFiles = currentSelected.files.filter((f) => f.rawFile)
        if (pendingFiles.length > 0) {
          finalFiles = await Promise.all(
            currentSelected.files.map(async (fileItem) => {
              if (!fileItem.rawFile) return fileItem

              const file = fileItem.rawFile
              const category = fileItem.category || 0

              // 0. Extract width/height for images natively before upload
              let width: number | undefined
              let height: number | undefined

              if (file.type.startsWith('image/')) {
                const img = new Image()
                img.src = URL.createObjectURL(file)
                await new Promise((resolve) => {
                  img.onload = () => {
                    width = img.width
                    height = img.height
                    URL.revokeObjectURL(img.src)
                    resolve(true)
                  }
                  img.onerror = () => {
                    URL.revokeObjectURL(img.src)
                    resolve(true)
                  }
                })
              }

              // 1. Get Presigned URL
              const presignedData = await getBuildingPresignedUrl({
                data: {
                  buildingId: Number(id),
                  count: 1,
                  category
                }
              })

              const { presignedUrl, key: s3Key } =
                presignedData.data.s3PreSignLinks[0]

              // 2. Upload to S3
              await axios.put(presignedUrl, file)

              // 3. Save to Metadata API
              const savedFileData = await saveFileMetadata({
                data: {
                  buildingId: Number(id),
                  category,
                  extension: fileItem.name.split('.').pop(),
                  name: fileItem.name,
                  mimeType: file.type,
                  size: file.size,
                  s3Path: s3Key,
                  extraInfo:
                    width && height ? JSON.stringify({ width, height }) : null,
                  takenAt: dayjs(file.lastModified).format(
                    'YYYY-MM-DDTHH:mm:ssZ'
                  ),
                  overwriteID: fileItem.overwriteID,
                  mode: fileItem.mode
                }
              })

              const resData = savedFileData.data
              return {
                id: resData.id,
                name: resData.name || fileItem.name,
                s3Path: resData.s3_path || resData.s3Path || s3Key,
                mimeType: resData.mime_type || resData.mimeType || file.type,
                extension:
                  resData.extension ||
                  file.name.split('.').pop()?.toLowerCase() ||
                  '',
                createdAt:
                  resData.created_at ||
                  resData.createdAt ||
                  new Date().toISOString(),
                size: resData.size,
                thumbnail:
                  resData.thumbnail ||
                  resData.s3_path ||
                  resData.s3Path ||
                  s3Key,
                category,
                pointId: resData.point_id || resData.pointId || null,
                takenAt: resData.taken_at || resData.takenAt || null
              }
            })
          )
        }
      }

      if (currentSelected?.files) {
        const renamedFiles = currentSelected.files.filter(
          (f) => f.id > 0 && f.originalName && f.originalName !== f.name
        )
        if (renamedFiles.length > 0) {
          await Promise.all(
            renamedFiles.map((fileItem) =>
              saveFileMetadata({
                method: 'patch',
                query: [fileItem.id.toString()],
                data: { name: fileItem.name }
              })
            )
          )
        }
      }

      if (selectedFolder === AnnotationType.INFO) {
        await handleAddEditAnnotation(
          undefined,
          memoContent
            ? CommonFunction.extractS3Key(memoContent)
            : CommonFunction.extractS3Key(currentSelected?.memo_content),
          selectedPdfFile?.name || currentSelected?.memo_file_name || '',
          finalFiles
        )
      } else if (selectedFolder === AnnotationType.IMAGE) {
        await handleAddEditAnnotation(
          key,
          memoContent
            ? CommonFunction.extractS3Key(memoContent)
            : CommonFunction.extractS3Key(currentSelected?.memo_content),
          selectedImageFile?.name || currentSelected?.memo_file_name || '',
          finalFiles
        )
      } else {
        await handleAddEditAnnotation(key, memoContent || '', '', finalFiles)
      }
    } catch (error: unknown) {
      setIsLoading(false)
      Utils.handleErrorNavigate(error, navigate)
    }
  }

  return {
    form,
    isLoading,
    isFetchingContent,
    setIsFetchingContent,
    isDataExistsAnnotation,
    dataSource,
    childrenFolder,
    quill,
    quillRef,
    QuillClass,
    currentSelected,
    setCurrentSelected,
    selectedFolder,
    setSelectedFolder,
    selectedChildrenFolder,
    setSelectedChildrenFolder,
    handleBack,
    handleCloseModal,
    validateAnnotationLink,
    handleSubmit,
    tempAlertData,
    setTempAlertData
  }
}
